import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaEnvelope, FaLock, FaTimes, FaUser, FaUserPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { app } from '../../firebase/firebase';

const MySwal = withReactContent(Swal);

const FormStoreUser = ({ onUserAdded, onCancel }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showSubmitLoaderButton, setShowSubmitLoaderButton] = useState(false);
  const auth = getAuth();

  const handlerOnSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password || !displayName) {
      MySwal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor completa todos los campos.',
      });
      return;
    }

    if (password.length < 6) {
      MySwal.fire({
        icon: 'warning',
        title: 'Contraseña inválida',
        text: 'La contraseña debe tener al menos 6 caracteres.',
      });
      return;
    }

    try {
      setShowSubmitLoaderButton(true);

      const authUser = auth.currentUser;
      if (!authUser) {
        throw new Error('Debes iniciar sesión como administrador para crear usuarios.');
      }

      const functions = getFunctions(app, 'us-central1');
      const createUserByAdmin = httpsCallable(functions, 'createUserByAdmin');
      const result = await createUserByAdmin({ email: normalizedEmail, password, displayName });
      const repairedExistingProfile = result?.data?.repairedExistingProfile === true;
      const invitationSent = result?.data?.invitationSent === true;

      await MySwal.fire({
        icon: invitationSent ? 'success' : 'warning',
        title: repairedExistingProfile ? 'Acceso completado' : 'Usuario creado',
        text: invitationSent
          ? `Se envió una invitación a ${normalizedEmail} para que cree su contraseña.`
          : `${repairedExistingProfile ? 'El perfil existente ya cuenta con acceso.' : 'El usuario y su acceso fueron creados.'} La invitación no pudo enviarse; puedes reenviarla desde la edición del usuario.`,
        confirmButtonText: 'OK',
      });

      setEmail('');
      setPassword('');
      setDisplayName('');

      if (onUserAdded) {
        onUserAdded();
      }
    } catch (error) {
      console.error('Error creando el usuario:', error);

      let errorMessage = 'No se pudo crear el usuario.';
      if (error?.code === 'functions/permission-denied') {
        errorMessage = 'No tienes permisos para crear usuarios.';
      } else if (error?.code === 'functions/unauthenticated') {
        errorMessage = 'Debes iniciar sesión para crear usuarios.';
      } else if (error?.code === 'functions/already-exists') {
        errorMessage = 'Este correo ya está registrado.';
      } else if (error?.code === 'functions/invalid-argument') {
        errorMessage = 'Datos inválidos. Verifica correo, contraseña y nombre.';
      } else if (error?.code === 'functions/internal') {
        errorMessage = 'Error interno del servidor al crear el usuario.';
      } else if (error?.code === 'functions/failed-precondition') {
        errorMessage = error?.message || 'El perfil requiere una revisión antes de crear su acceso.';
      }

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Este correo ya está registrado.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña es demasiado débil.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El correo electrónico no es válido.';
      }

      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setShowSubmitLoaderButton(false);
    }
  };

  return (
    <Form className="master-modal-form" onSubmit={handlerOnSubmit}>
      <div className="master-modal-form__grid master-modal-form__grid--single">
        <Form.Group className="master-modal-field" controlId="displayName">
          <Form.Label>Nombre completo</Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaUser />
            </InputGroup.Text>
            <Form.Control
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              required
            />
          </InputGroup>
        </Form.Group>

        <Form.Group className="master-modal-field" controlId="email">
          <Form.Label>Correo electrónico</Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaEnvelope />
            </InputGroup.Text>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </InputGroup>
        </Form.Group>

        <Form.Group className="master-modal-field" controlId="password">
          <Form.Label>Contraseña</Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaLock />
            </InputGroup.Text>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </InputGroup>
        </Form.Group>
      </div>

      <div className="master-user-modal__footer">
        <Button
          type="button"
          variant="outline-primary"
          className="btn-transition"
          onClick={onCancel}
        >
          <FaTimes /> Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="btn-transition"
          disabled={showSubmitLoaderButton}
        >
          {showSubmitLoaderButton ? (
            <>
              <Spinner animation="border" size="sm" />
              Creando...
            </>
          ) : (
            <>
              <FaUserPlus /> Crear usuario
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};

export { FormStoreUser };
